import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface FieldHelpOpenCtx {
  openId: string | null;
  toggle: (id: string) => void;
}

const FieldHelpOpenContext = createContext<FieldHelpOpenCtx | null>(null);

/** Wraps a form or section so only one FieldHelp hint is open at a time. */
export function FieldHelpGroup({ children }: { children: ReactNode }): JSX.Element {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggle = useCallback((id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);
  const value = useMemo(() => ({ openId, toggle }), [openId, toggle]);
  return (
    <FieldHelpOpenContext.Provider value={value}>{children}</FieldHelpOpenContext.Provider>
  );
}

function useFieldHelpOpen(id: string): { open: boolean; toggle: () => void } {
  const ctx = useContext(FieldHelpOpenContext);
  const [localOpen, setLocalOpen] = useState(false);
  if (ctx) {
    return {
      open: ctx.openId === id,
      toggle: () => ctx.toggle(id),
    };
  }
  return {
    open: localOpen,
    toggle: () => setLocalOpen((v) => !v),
  };
}

function InfoButton({
  open,
  onToggle,
  controlsId,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  controlsId: string;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`field-help__info${open ? ' field-help__info--open' : ''}`}
      aria-label={`Help: ${label}`}
      aria-expanded={open}
      aria-controls={controlsId}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
    >
      i
    </button>
  );
}

interface FieldHelpProps {
  label: string;
  help: string;
  htmlFor?: string;
  children: ReactNode;
  /** Extra class on the outer wrapper (e.g. for grid cells). */
  className?: string;
}

/** Label + (i) info button + control + expandable hint. */
export function FieldHelp({
  label,
  help,
  htmlFor,
  children,
  className,
}: FieldHelpProps): JSX.Element {
  const reactId = useId();
  const helpId = `${reactId}-hint`;
  const { open, toggle } = useFieldHelpOpen(reactId);

  return (
    <div className={['field-help', className].filter(Boolean).join(' ')}>
      <div className="field-help__label-row">
        {htmlFor ? (
          <label className="field-help__label" htmlFor={htmlFor}>
            {label}
          </label>
        ) : (
          <span className="field-help__label">{label}</span>
        )}
        <InfoButton open={open} onToggle={toggle} controlsId={helpId} label={label} />
      </div>
      {children}
      {open ? (
        <p id={helpId} className="field-help__hint" role="note">
          {help}
        </p>
      ) : null}
    </div>
  );
}

interface SectionHelpProps {
  title: string;
  help: string;
  children?: ReactNode;
  /** Use legend inside a fieldset; otherwise renders an h3 title row. */
  as?: 'h3' | 'legend';
  className?: string;
}

/** Section/legend title + (i) explaining a group of checkbox options; children = the controls. */
export function SectionHelp({
  title,
  help,
  children,
  as = 'h3',
  className,
}: SectionHelpProps): JSX.Element {
  const reactId = useId();
  const helpId = `${reactId}-hint`;
  const { open, toggle } = useFieldHelpOpen(reactId);

  const hint = open ? (
    <p id={helpId} className="field-help__hint" role="note">
      {help}
    </p>
  ) : null;

  if (as === 'legend') {
    return (
      <>
        <legend
          className={['field-help__legend', 'field-help__title-row', className]
            .filter(Boolean)
            .join(' ')}
        >
          <span className="field-help__title">{title}</span>
          <InfoButton open={open} onToggle={toggle} controlsId={helpId} label={title} />
        </legend>
        {hint}
        {children}
      </>
    );
  }

  return (
    <div className={['field-help field-help--section', className].filter(Boolean).join(' ')}>
      <div className="field-help__title-row">
        <h3 className="field-help__title">{title}</h3>
        <InfoButton open={open} onToggle={toggle} controlsId={helpId} label={title} />
      </div>
      {hint}
      {children}
    </div>
  );
}
